import { useState, useEffect } from 'react';

// Environment variables
const N8N_URL = process.env.REACT_APP_N8N_URL;

export const useInspector = (shadowRef, selectedElements, setSelectedElements, htmlContent) => {
  // Dummy effect to check if any effect runs
  useEffect(() => {
    console.log('[Dummy Effect] useEffect at top of useInspector');
  }, []);

  console.log('[useInspector] Hook called');

  // Log before highlight effect
  console.log('[useInspector] Before highlight effect declaration');

  const [activeInspectorItem, setActiveInspectorItem] = useState(null);
  const [editingPath, setEditingPath] = useState({});
  const [editingValue, setEditingValue] = useState({});
  const [editingError, setEditingError] = useState({});
  const [aiMagicLoading, setAiMagicLoading] = useState(false);

  const getInspectorColor = (id) => {
    const colors = {
      title: '#2196F3',
      url: '#4CAF50',
      content: '#FF9800',
      summary: '#9C27B0',
      pubdate: '#F44336',
      pagination: '#607D8B',
      citation: '#795548',
      category: '#FFB74D'
    };
    return colors[id] || '#666';
  };

  // Helper function to suggest better selectors
  const suggestBetterSelector = (element, isContext = false) => {
    if (!element) return null;
    
    // Check for data attributes (often more reliable than classes)
    const dataAttrs = ['data-testid', 'data-cy', 'data-test', 'data-id', 'data-selector'];
    for (const attr of dataAttrs) {
      if (element.hasAttribute(attr)) {
        return `[${attr}="${element.getAttribute(attr)}"]`;
      }
    }
    
    // Check for role attribute
    if (element.hasAttribute('role')) {
      return `[role="${element.getAttribute('role')}"]`;
    }
    
    // Check for aria-label
    if (element.hasAttribute('aria-label')) {
      return `[aria-label="${element.getAttribute('aria-label')}"]`;
    }
    
    // For context selection, look for patterns that suggest repeated items
    if (isContext) {
      const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
      if (shadowRoot) {
        // Check if this element has similar siblings (indicating a repeated pattern)
        if (element.parentElement) {
          const siblings = Array.from(element.parentElement.children);
          const sameTagSiblings = siblings.filter(sibling => 
            sibling.nodeName.toLowerCase() === element.nodeName.toLowerCase()
          );
          
          if (sameTagSiblings.length > 1) {
            // This looks like a repeated pattern, try to find a class-based selector
            if (element.className && typeof element.className === 'string') {
              const classes = Array.from(element.classList).filter(cls => 
                cls.trim() && 
                cls.length > 2 &&
                !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate|translate-)/)
              );
              
              if (classes.length > 0) {
                const selector = `${element.nodeName.toLowerCase()}.${classes[0]}`;
                // Test if this selector matches multiple elements (good for context)
                const matches = shadowRoot.querySelectorAll(selector);
                if (matches.length > 1) {
                  return selector;
                }
              }
            }
          }
        }
      }
    }
    
    // Try to suggest a simpler selector by removing nth-child when possible
    if (element.className && typeof element.className === 'string') {
      const classes = Array.from(element.classList).filter(cls => 
        cls.trim() && 
        cls.length > 2 &&
        !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate|translate-)/)
      );
      
      if (classes.length > 0) {
        const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
        if (shadowRoot) {
          // Try with single class first
          const singleClassSelector = `${element.nodeName.toLowerCase()}.${classes[0]}`;
          const singleMatches = shadowRoot.querySelectorAll(singleClassSelector);
          
          // If single class selector matches multiple elements, that's good for repeated content
          if (singleMatches.length > 1) {
            return singleClassSelector;
          }
          
          // If single class selector is unique, that's perfect
          if (singleMatches.length === 1) {
            return singleClassSelector;
          }
          
          // Try with multiple classes if available
          if (classes.length > 1) {
            const multiClassSelector = `${element.nodeName.toLowerCase()}.${classes.slice(0, 2).join('.')}`;
            const multiMatches = shadowRoot.querySelectorAll(multiClassSelector);
            if (multiMatches.length === 1) {
              return multiClassSelector;
            }
          }
        }
      }
    }
    
    return null;
  };

  const getUniqueSelector = (el, isContext = false) => {
    if (!el) return '';
    
    // If element has an ID, use it (most reliable)
    if (el.id) return `#${el.id}`;
    
    // Try to find a unique class-based selector
    if (el.className && typeof el.className === 'string') {
      const classes = Array.from(el.classList).filter(cls => cls.trim());
      
      // Look for meaningful class names (avoid utility classes)
      const meaningfulClasses = classes.filter(cls => 
        !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate-|translate-)/) &&
        cls.length > 2
      );
      
      if (meaningfulClasses.length > 0) {
        // Try with the most meaningful class first
        const primaryClass = meaningfulClasses[0];
        const selector = `${el.nodeName.toLowerCase()}.${primaryClass}`;
        
        // Test if this selector is unique
        const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
        if (shadowRoot) {
          const matches = shadowRoot.querySelectorAll(selector);
          if (matches.length === 1) {
            return selector;
          }
        }
        
        // If not unique, try with multiple meaningful classes
        if (meaningfulClasses.length > 1) {
          const multiClassSelector = `${el.nodeName.toLowerCase()}.${meaningfulClasses.slice(0, 2).join('.')}`;
          if (shadowRoot) {
            const matches = shadowRoot.querySelectorAll(multiClassSelector);
            if (matches.length === 1) {
              return multiClassSelector;
            }
          }
        }
      }
    }
    
    // Fallback: build a short path with meaningful selectors
    let path = [];
    let current = el;
    let depth = 0;
    const maxDepth = 3; // Limit depth for simplicity
    
    while (current && current.nodeType === 1 && depth < maxDepth) {
      let selector = current.nodeName.toLowerCase();
      
      // Add meaningful class if available
      if (current.className && typeof current.className === 'string') {
        const classes = Array.from(current.classList).filter(cls => 
          cls.trim() && 
          cls.length > 2 &&
          !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate-|translate-)/)
        );
        
        if (classes.length > 0) {
          selector += '.' + classes[0]; // Use only the first meaningful class
        }
      }
      
      // For context selection, avoid nth-child to select all similar elements
      // For other selections, be smarter about when to add nth-child
      if (!isContext && current.parentElement) {
        const siblings = Array.from(current.parentElement.children);
        const sameTagSiblings = siblings.filter(sibling => 
          sibling.nodeName.toLowerCase() === current.nodeName.toLowerCase()
        );
        
        // Only add nth-child if it's absolutely necessary for uniqueness
        if (sameTagSiblings.length > 1) {
          // Check if we can make the selector unique without nth-child
          const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
          
          if (shadowRoot) {
            // Try to find a more specific selector by using multiple classes
            if (current.className && typeof current.className === 'string') {
              const classes = Array.from(current.classList).filter(cls => 
                cls.trim() && 
                cls.length > 2 &&
                !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate-|translate-)/)
              );
              
              if (classes.length > 1) {
                // Try with multiple classes first
                const multiClassSelector = `${current.nodeName.toLowerCase()}.${classes.slice(0, 2).join('.')}`;
                try {
                  const matches = shadowRoot.querySelectorAll(multiClassSelector);
                  if (matches.length === 1) {
                    // Replace the current selector with the more specific one
                    selector = multiClassSelector;
                  } else {
                    // If still not unique, then add nth-child as last resort
                    const index = sameTagSiblings.indexOf(current) + 1;
                    selector += `:nth-child(${index})`;
                  }
                } catch (e) {
                  // Fallback to nth-child
                  const index = sameTagSiblings.indexOf(current) + 1;
                  selector += `:nth-child(${index})`;
                }
              } else {
                // Only one class, add nth-child
                const index = sameTagSiblings.indexOf(current) + 1;
                selector += `:nth-child(${index})`;
              }
            } else {
              // No classes, add nth-child
              const index = sameTagSiblings.indexOf(current) + 1;
              selector += `:nth-child(${index})`;
            }
          } else {
            // No shadow root, add nth-child as fallback
            const index = sameTagSiblings.indexOf(current) + 1;
            selector += `:nth-child(${index})`;
          }
        }
      }
      
      path.unshift(selector);
      current = current.parentElement;
      depth++;
    }
    
    return path.join(' > ');
  };

  const getRelativePath = (contextElement, targetElement) => {
    if (!contextElement || !targetElement) return '';
    if (contextElement === targetElement) return '';
    
    let path = [];
    let current = targetElement;
    
    // Walk up the DOM tree until we reach the context element
    while (current && current !== contextElement && current.parentElement) {
      let selector = current.nodeName.toLowerCase();
      
      // Add meaningful class if available (avoid utility classes)
      if (current.className && typeof current.className === 'string') {
        const classes = Array.from(current.classList).filter(cls => 
          cls.trim() && 
          cls.length > 2 &&
          !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate-|translate-)/)
        );
        
        if (classes.length > 0) {
          // Use only the most meaningful class to keep selector simple
          selector += '.' + classes[0];
        }
      }
      
      // For relative paths within context, be smarter about when to add nth-child
      if (current.parentElement) {
        const siblings = Array.from(current.parentElement.children);
        const sameTagSiblings = siblings.filter(sibling => 
          sibling.nodeName.toLowerCase() === current.nodeName.toLowerCase()
        );
        
        if (sameTagSiblings.length > 1) {
          // Try to make the selector unique without nth-child first
          const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
          
          if (shadowRoot) {
            // Try with multiple classes first
            if (current.className && typeof current.className === 'string') {
              const classes = Array.from(current.classList).filter(cls => 
                cls.trim() && 
                cls.length > 2 &&
                !cls.match(/^(w-|h-|p-|m-|text-|bg-|border-|rounded-|flex|grid|hidden|block|inline|relative|absolute|fixed|z-|opacity-|transition-|transform-|scale-|rotate-|translate-)/)
              );
              
              if (classes.length > 1) {
                // Try with multiple classes
                const multiClassSelector = `${current.nodeName.toLowerCase()}.${classes.slice(0, 2).join('.')}`;
                try {
                  // Test if this selector is unique within the context
                  const matches = contextElement.querySelectorAll(multiClassSelector);
                  if (matches.length === 1) {
                    // Replace the current selector with the more specific one
                    selector = multiClassSelector;
                  } else {
                    // If still not unique, then add nth-child as last resort
                    const index = sameTagSiblings.indexOf(current) + 1;
                    selector += `:nth-child(${index})`;
                  }
                } catch (e) {
                  // Fallback to nth-child
                  const index = sameTagSiblings.indexOf(current) + 1;
                  selector += `:nth-child(${index})`;
                }
              } else {
                // Only one class, add nth-child
                const index = sameTagSiblings.indexOf(current) + 1;
                selector += `:nth-child(${index})`;
              }
            } else {
              // No classes, add nth-child
              const index = sameTagSiblings.indexOf(current) + 1;
              selector += `:nth-child(${index})`;
            }
          } else {
            // No shadow root, add nth-child as fallback
            const index = sameTagSiblings.indexOf(current) + 1;
            selector += `:nth-child(${index})`;
          }
        }
      }
      
      path.unshift(selector);
      current = current.parentElement;
    }
    
    return path.join(' > ');
  };

  const updateInspectorSelection = (itemId, selector) => {
    setSelectedElements(prev => ({ ...prev, [itemId]: selector }));
  };

  const handleEditingPathChange = (itemId, isEditing) => {
    setEditingPath(prev => ({ ...prev, [itemId]: isEditing }));
    if (isEditing) {
      setEditingValue(prev => ({ ...prev, [itemId]: selectedElements[itemId] || '' }));
      setEditingError(prev => ({ ...prev, [itemId]: '' }));
    }
  };

  const handleEditingValueChange = (itemId, value) => {
    setEditingValue(prev => ({ ...prev, [itemId]: value }));
    
    // Real-time validation
    if (!value.trim()) {
      setEditingError(prev => ({ ...prev, [itemId]: '' }));
      return;
    }
    
    // Test the selector in real-time
    const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
    if (shadowRoot) {
      try {
        const elements = shadowRoot.querySelectorAll(value);
        if (elements.length === 0) {
          setEditingError(prev => ({ ...prev, [itemId]: 'No elements found with this selector' }));
        } else {
          setEditingError(prev => ({ ...prev, [itemId]: '' }));
        }
      } catch (e) {
        setEditingError(prev => ({ ...prev, [itemId]: 'Invalid CSS selector' }));
      }
    }
  };

  const handleEditingPathSave = (itemId) => {
    const value = editingValue[itemId];
    if (!value.trim()) {
      setEditingError(prev => ({ ...prev, [itemId]: 'Selector cannot be empty' }));
      return;
    }
    
    // Test the selector
    const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
    if (shadowRoot) {
      try {
        const elements = shadowRoot.querySelectorAll(value);
        if (elements.length === 0) {
          setEditingError(prev => ({ ...prev, [itemId]: 'No elements found with this selector' }));
          return;
        }
      } catch (e) {
        setEditingError(prev => ({ ...prev, [itemId]: 'Invalid CSS selector' }));
        return;
      }
    }
    
    updateInspectorSelection(itemId, value);
    setEditingPath(prev => ({ ...prev, [itemId]: false }));
  };

  const handleEditingPathCancel = (itemId) => {
    setEditingPath(prev => ({ ...prev, [itemId]: false }));
    setEditingError(prev => ({ ...prev, [itemId]: '' }));
  };

  // Shadow DOM inspection logic
  useEffect(() => {
    console.log('[Inspector Highlight Effect] useEffect triggered');
    console.log('[Inspector Highlight Effect] Running. selectedElements:', selectedElements, 'htmlContent length:', htmlContent ? htmlContent.length : 0);
    const ref = shadowRef.current && shadowRef.current.shadowRoot;
    if (!ref) return;

    // Remove all previous highlights
    ref.querySelectorAll('.inspector-hover').forEach(el => el.classList.remove('inspector-hover'));
    ref.querySelectorAll('[data-inspector-selected]').forEach(el => {
      el.style.outline = '';
      el.removeAttribute('data-inspector-selected');
    });

    // Restore selected highlights for all inspector items
    Object.entries(selectedElements).forEach(([inspectorId, selector]) => {
      try {
        // For context-dependent items, only highlight elements within the context
        const contextDependentItems = ['title', 'url', 'pubdate', 'summary', 'citation'];
        if (contextDependentItems.includes(inspectorId)) {
          const contextPath = selectedElements['context'];
          if (contextPath && contextPath.trim()) {
            // Find context elements first
            const contextElements = ref.querySelectorAll(contextPath);
            console.log(`Found ${contextElements.length} context elements for ${inspectorId}`);
            
            // For each context element, find matching elements within it
            contextElements.forEach(contextEl => {
              try {
                const elementsWithinContext = contextEl.querySelectorAll(selector);
                console.log(`Highlighting for ${inspectorId} within context: found ${elementsWithinContext.length} elements`);
                elementsWithinContext.forEach(sel => {
                  sel.setAttribute('data-inspector-selected', inspectorId);
                  sel.style.outline = `3px solid ${getInspectorColor(inspectorId)}`;
                });
              } catch (contextError) {
                console.warn('Invalid selector within context for', inspectorId, ':', selector);
              }
            });
          } else {
            // No context selected, don't highlight anything for context-dependent items
            console.log(`No context selected for ${inspectorId}, skipping highlight`);
          }
        } else {
          // For non-context-dependent items, highlight globally
          const elements = ref.querySelectorAll(selector);
          console.log(`Highlighting for ${inspectorId} with selector "${selector}": found ${elements.length} elements`);
          elements.forEach(sel => {
            sel.setAttribute('data-inspector-selected', inspectorId);
            sel.style.outline = `3px solid ${getInspectorColor(inspectorId)}`;
          });
        }
      } catch (e) {
        console.warn('Invalid selector for', inspectorId, ':', selector);
      }
    });

    if (!activeInspectorItem) return;

    let lastHovered = null;
    
    // Only allow hover in inspection mode
    function mouseoverHandler(e) {
      if (!activeInspectorItem) return;
      if (e.target === ref) return;
      if (e.target.tagName === 'HTML' || e.target.tagName === 'BODY') return;
      if (e.target.tagName === 'HEAD' || e.target.tagName === 'META' || e.target.tagName === 'LINK') return;
      
      if (lastHovered) {
        lastHovered.classList.remove('inspector-hover');
      }
      
      if (!e.target.hasAttribute('data-inspector-selected')) {
        e.target.classList.add('inspector-hover');
        lastHovered = e.target;
      }
    }
    
    function mouseoutHandler(e) {
      if (!activeInspectorItem) return;
      if (e.target === lastHovered) {
        lastHovered.classList.remove('inspector-hover');
        lastHovered = null;
      }
    }
    
    function clickHandler(e) {
      if (!activeInspectorItem) return;
      e.preventDefault();
      e.stopPropagation();
      
      // Remove previous selection for this inspector item
      ref.querySelectorAll(`[data-inspector-selected="${activeInspectorItem}"]`).forEach(el => {
        el.style.outline = '';
        el.removeAttribute('data-inspector-selected');
      });
      
      if (lastHovered) {
        lastHovered.classList.remove('inspector-hover');
      }
      
      e.target.setAttribute('data-inspector-selected', activeInspectorItem);
      e.target.style.outline = `3px solid ${getInspectorColor(activeInspectorItem)}`;
      
      // Try to get a better selector first
      const isContext = activeInspectorItem === 'context';
      let selector = suggestBetterSelector(e.target, isContext);
      
      // If no better selector found, use the unique selector
      if (!selector) {
        // For context selection, avoid nth-child to select all similar elements
        selector = getUniqueSelector(e.target, isContext);
      }
      
      // Log the selector for debugging
      if (isContext) {
        console.log(`Context selector generated: ${selector}`);
        // Test the selector to see how many elements it matches
        const shadowRoot = shadowRef.current && shadowRef.current.shadowRoot;
        if (shadowRoot) {
          try {
            const matches = shadowRoot.querySelectorAll(selector);
            console.log(`Context selector matches ${matches.length} elements`);
          } catch (error) {
            console.error('Error testing context selector:', error);
          }
        }
      }
      
      // For title, url, pubdate, summary, citation, and category, make the path relative to the context
      const contextDependentItems = ['title', 'url', 'pubdate', 'summary', 'citation', 'category'];
      if (contextDependentItems.includes(activeInspectorItem)) {
        const contextPath = selectedElements['context'];
        if (contextPath && contextPath.trim()) {
          // Check if the selected element is within the context
          try {
            const contextElements = ref.querySelectorAll(contextPath);
            let isWithinContext = false;
            
            contextElements.forEach(contextEl => {
              if (contextEl.contains(e.target)) {
                isWithinContext = true;
              }
            });
            
            if (isWithinContext) {
              let containingContext = Array.from(contextElements).find(contextEl => contextEl.contains(e.target));
              if (containingContext) {
                // Create a relative path from the correct context to the selected element
                const relativePath = getRelativePath(containingContext, e.target);
                updateInspectorSelection(activeInspectorItem, relativePath);
                console.log(`Selected ${activeInspectorItem} with relative path: ${relativePath}`);
              } else {
                // Element is not within any context, use absolute path
                updateInspectorSelection(activeInspectorItem, selector);
                console.log(`Selected ${activeInspectorItem} with absolute path: ${selector}`);
              }
            } else {
              // Element is not within context, use absolute path
              updateInspectorSelection(activeInspectorItem, selector);
              console.log(`Selected ${activeInspectorItem} with absolute path (not in context): ${selector}`);
            }
          } catch (error) {
            // Fallback to absolute path if context selector is invalid
            updateInspectorSelection(activeInspectorItem, selector);
            console.log(`Selected ${activeInspectorItem} with fallback path: ${selector}`);
          }
        } else {
          // No context selected, use absolute path
          updateInspectorSelection(activeInspectorItem, selector);
          console.log(`Selected ${activeInspectorItem} with absolute path (no context): ${selector}`);
        }
      } else {
        // For other items, use absolute path
        updateInspectorSelection(activeInspectorItem, selector);
        console.log(`Selected ${activeInspectorItem} with absolute path: ${selector}`);
      }
      setActiveInspectorItem(null);
    }

    ref.addEventListener('mouseover', mouseoverHandler);
    ref.addEventListener('mouseout', mouseoutHandler);
    ref.addEventListener('click', clickHandler);

    return () => {
      ref.removeEventListener('mouseover', mouseoverHandler);
      ref.removeEventListener('mouseout', mouseoutHandler);
      ref.removeEventListener('click', clickHandler);
    };
  }, [activeInspectorItem, selectedElements, shadowRef, htmlContent]);

  // Add CSS for inspector hover effect
  useEffect(() => {
    const ref = shadowRef.current && shadowRef.current.shadowRoot;
    if (!ref) return;

    // Remove any existing inspector styles
    const existingStyle = ref.querySelector('#inspector-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = 'inspector-styles';
    style.textContent = `
      .inspector-hover {
        outline: 3px dashed #ff9800 !important;
        outline-offset: 2px !important;
        cursor: pointer !important;
        background-color: rgba(255, 152, 0, 0.1) !important;
        position: relative !important;
        z-index: 1000 !important;
        transition: all 0.2s ease !important;
      }
      .inspector-hover::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: rgba(255, 152, 0, 0.2);
        border-radius: 2px;
        z-index: -1;
        pointer-events: none;
      }
      .inspector-hover:hover {
        outline: 3px solid #ff9800 !important;
        background-color: rgba(255, 152, 0, 0.2) !important;
      }
    `;
    ref.appendChild(style);

    return () => {
      if (ref.contains(style)) {
        ref.removeChild(style);
      }
    };
  }, [shadowRef, htmlContent]);

  // --- AI Magic (n8n) Handlers ---
  const handleInspectorAIMatch = async (currentPreviewUrl) => {
    if (!currentPreviewUrl || aiMagicLoading) return;
    setAiMagicLoading(true);
    try {
      const n8nRes = await fetch(N8N_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: currentPreviewUrl })
      });
      if (n8nRes.ok) {
        const n8nData = await n8nRes.json();
        const updates = {};
        if (n8nData.article_title_path_selector) {
          updates.title = n8nData.article_title_path_selector;
        }
        if (n8nData.article_publication_date_path_selector) {
          updates.pubdate = n8nData.article_publication_date_path_selector;
        }
        if (n8nData.article_summary_path_selector) {
          updates.summary = n8nData.article_summary_path_selector;
        }
        if (n8nData.next_page_url_path_selector) {
          updates.pagination = n8nData.next_page_url_path_selector;
        }
        if (n8nData.article_url_path_selector) {
          updates.url = n8nData.article_url_path_selector;
        }
        if (n8nData.article_context_path_selector) {
          updates.context = n8nData.article_context_path_selector;
        }
        if (n8nData.article_category_path_selector) {
          updates.category = n8nData.article_category_path_selector;
        }
        if (Object.keys(updates).length > 0) {
          setSelectedElements(prev => ({ ...prev, ...updates }));
        }
      }
    } catch (e) {
      // Ignore n8n errors
    } finally {
      setAiMagicLoading(false);
    }
  };

  const handleInspectorAIMatchItem = async (itemId, currentPreviewUrl) => {
    if (!currentPreviewUrl || aiMagicLoading) return;
    setAiMagicLoading(true);
    try {
      const n8nRes = await fetch(N8N_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: currentPreviewUrl })
      });
      if (n8nRes.ok) {
        const n8nData = await n8nRes.json();
        let match = null;
        if (itemId === 'title' && n8nData.article_title_path_selector) match = n8nData.article_title_path_selector;
        if (itemId === 'pubdate' && n8nData.article_publication_date_path_selector) match = n8nData.article_publication_date_path_selector;
        if (itemId === 'summary' && n8nData.article_summary_path_selector) match = n8nData.article_summary_path_selector;
        if (itemId === 'pagination' && n8nData.next_page_url_path_selector) match = n8nData.next_page_url_path_selector;
        if (itemId === 'url' && n8nData.article_url_path_selector) match = n8nData.article_url_path_selector;
        if (itemId === 'context' && n8nData.article_context_path_selector) match = n8nData.article_context_path_selector;
        if (itemId === 'category' && n8nData.article_category_path_selector) match = n8nData.article_category_path_selector;
        if (match) {
          setSelectedElements(prev => ({ ...prev, [itemId]: match }));
        }
      }
    } catch (e) {
      // Ignore n8n errors
    } finally {
      setAiMagicLoading(false);
    }
  };

  return {
    activeInspectorItem,
    editingPath,
    editingValue,
    editingError,
    aiMagicLoading,
    getInspectorColor,
    setActiveInspectorItem,
    handleEditingPathChange,
    handleEditingValueChange,
    handleEditingPathSave,
    handleEditingPathCancel,
    handleInspectorAIMatch,
    handleInspectorAIMatchItem,
    updateInspectorSelection
  };
}; 